import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Smartphone, Monitor } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const { translations } = useLanguage();

  useEffect(() => {
    // Check if already installed
    const isInstalledPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isInstalledChrome = !!window.chrome?.app?.isInstalled;

    // Check if desktop
    setIsDesktop(!/Android|iPhone|iPad|iPod/i.test(navigator.userAgent));

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show prompt if not installed
    if (!isInstalledPWA && !isInstalledChrome) {
      setShowPrompt(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setShowPrompt(false);
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error installing app:', error);
      }
    } else {
      // Browser-specific instructions
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // iOS Safari
        alert('To install the app:\n1. Tap the Share button\n2. Scroll down and tap "Add to Home Screen"');
      } else if (/Chrome/i.test(navigator.userAgent) && isDesktop) {
        // Desktop Chrome
        alert('To install the app:\n1. Click the three dots menu in Chrome\n2. Click "Install Ithraa Operation..."');
      } else if (/Firefox/i.test(navigator.userAgent) && isDesktop) {
        // Desktop Firefox
        alert('To install the app:\n1. Click the three dots menu in Firefox\n2. Click "Install Site as Application"');
      } else if (/Edge/i.test(navigator.userAgent) && isDesktop) {
        // Desktop Edge
        alert('To install the app:\n1. Click the three dots menu in Edge\n2. Click "Apps" > "Install this site as an app"');
      }
    }
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className={`fixed ${isDesktop ? 'top-4 right-4 max-w-sm' : 'bottom-4 left-4 right-4 max-w-md mx-auto'} z-50`}
      >
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-50 rounded-lg">
                {isDesktop ? (
                  <Monitor className="text-primary-500" size={24} />
                ) : (
                  <Smartphone className="text-primary-500" size={24} />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {translations?.installPrompt?.title || 'Install App'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {isDesktop
                    ? (translations?.installPrompt?.desktopDescription || 'Install for quick access from your desktop')
                    : (translations?.installPrompt?.mobileDescription || 'Add to your home screen for easy access')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPrompt(false)}
              className="text-gray-400 hover:text-gray-500 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="mt-4 flex gap-3 justify-end">
            <button
              onClick={() => setShowPrompt(false)}
              className="text-gray-600 hover:text-gray-800 text-sm px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {translations?.installPrompt?.notNow || 'Not now'}
            </button>
            <button
              onClick={handleInstall}
              className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download size={18} />
              <span>
                {translations?.installPrompt?.install || 'Install'}
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};